import { useState, useEffect } from "react";
import toast from "../../../utils/toast";
import {
  getVacateRequestList,
  getVacateRequestById,
  updateVacateRequestById,
  approveVacateRequest,
  rejectVacateRequest,
  getVacateNoticeSetting,
  updateVacateNoticeSetting,
} from "../../../services/propertyOwner/propertyOwnerServices";

export default function VacateRequest() {
  const [vacateData, setVacateData] = useState(null);
  const [listLoading, setListLoading] = useState(true);
  const [isModalOpenApprove, setIsModalOpenApprove] = useState(false);
  const [isModalOpenReject, setIsModalOpenReject] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [approveRemark, setApproveRemark] = useState("");
  const [rejectRemark, setRejectRemark] = useState("");
  const [isModalOpenSettings, setIsModalOpenSettings] = useState(false);
  const [settingsForm, setSettingsForm] = useState({
    minimumNoticePeriod: 15,
    shortNoticePenalty: 500,
    autoApprove: false,
  });
  const [settingsLoading, setSettingsLoading] = useState(false);
  const [isModalOpenEdit, setIsModalOpenEdit] = useState(false);
  const [editForm, setEditForm] = useState({
    plannedLeaveDate: "",
    freeHoldDate: "",
    ownerNotes: "",
  });
  const [editLoading, setEditLoading] = useState(false);
  const [selectedEditRequest, setSelectedEditRequest] = useState(null);

  useEffect(() => {
    fetchVacateRequestList();
    fetchVacateSettings();
  }, []);

  const fetchVacateRequestList = async () => {
    setListLoading(true);
    try {
      const res = await getVacateRequestList();
      if (res.statusCode === 1) setVacateData(res.data);
    } catch (err) {
      console.log("Failed to fetch vacate requests");
    } finally {
      setListLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const getStatusBadge = (status) => {
    if (status === 1)
      return { label: "⏳ Pending", bg: "#FEF3C7", color: "#92400E" };
    if (status === 2)
      return { label: "✓ Approved", bg: "#EAF3DE", color: "#27500A" };
    if (status === 3)
      return { label: "✕ Rejected", bg: "#FCEBEB", color: "#A32D2D" };
    if (status === 4)
      return { label: "↩ Cancelled", bg: "#F3F4F6", color: "#6B7280" };
    return { label: "○ Unknown", bg: "#F3F4F6", color: "#6B7280" };
  };

  const handleApprove = async () => {
    setActionLoading(true);
    try {
      const res = await approveVacateRequest(selectedRequestId, {
        ownerRemark: approveRemark,
      });
      if (res.statusCode === 1) {
        toast.success(res.message);
        setIsModalOpenApprove(false);
        setSelectedRequestId(null);
        setApproveRemark("");
        fetchVacateRequestList();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setActionLoading(false);
    }
  };

  const handleReject = async () => {
    setActionLoading(true);
    try {
      const res = await rejectVacateRequest(selectedRequestId, {
        ownerRemark: rejectRemark,
      });
      if (res.statusCode === 1) {
        toast.success(res.message);
        setIsModalOpenReject(false);
        setSelectedRequestId(null);
        setRejectRemark("");
        fetchVacateRequestList();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setActionLoading(false);
    }
  };

  const fetchVacateSettings = async () => {
    try {
      const res = await getVacateNoticeSetting();
      if (res.statusCode === 1) {
        setSettingsForm({
          minimumNoticePeriod: res.data.minimumNoticePeriod ?? 15,
          shortNoticePenalty: res.data.shortNoticePenalty ?? 500,
          autoApprove: res.data.autoApprove ?? false,
        });
      }
    } catch (err) {
      console.log("Failed to fetch vacate settings");
    }
  };

  const handleSettingsChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettingsForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSaveSettings = async () => {
    setSettingsLoading(true);
    try {
      const res = await updateVacateNoticeSetting({
        minimumNoticePeriod: Number(settingsForm.minimumNoticePeriod),
        shortNoticePenalty: Number(settingsForm.shortNoticePenalty),
        autoApprove: settingsForm.autoApprove,
      });
      if (res.statusCode === 1) {
        toast.success(res.message);
        setIsModalOpenSettings(false);
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setSettingsLoading(false);
    }
  };

  const openEditModal = async (id) => {
    try {
      const res = await getVacateRequestById(id);
      if (res.statusCode === 1) {
        const d = res.data;
        setSelectedEditRequest(d);
        setEditForm({
          plannedLeaveDate: d.plannedLeaveDate
            ? d.plannedLeaveDate.split("T")[0]
            : "",
          freeHoldDate: d.freeHoldDate ? d.freeHoldDate.split("T")[0] : "",
          ownerNotes: d.ownerNotes || "",
        });
        setIsModalOpenEdit(true);
      }
    } catch (err) {
      toast.error("Failed to load vacate request.");
    }
  };

  const closeEditModal = () => {
    setIsModalOpenEdit(false);
    setSelectedEditRequest(null);
    setEditForm({ plannedLeaveDate: "", freeHoldDate: "", ownerNotes: "" });
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSaveEdit = async () => {
    setEditLoading(true);
    try {
      const payload = {
        plannedLeaveDate: editForm.plannedLeaveDate
          ? new Date(editForm.plannedLeaveDate).toISOString()
          : null,
        freeHoldDate: editForm.freeHoldDate
          ? new Date(editForm.freeHoldDate).toISOString()
          : null,
        ownerNotes: editForm.ownerNotes,
      };
      const res = await updateVacateRequestById(
        selectedEditRequest.id,
        payload,
      );
      if (res.statusCode === 1) {
        toast.success(res.message);
        closeEditModal();
        fetchVacateRequestList();
      } else {
        toast.error(res.message);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || "Something went wrong.");
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <>
      <div className="main">
        <div className="topbar">
          <div>
            <div className="page-title">Vacate Requests</div>
            <div className="page-sub">
              Review and manage tenant vacate notices
            </div>
          </div>
          <div className="topbar-right">
            <button
              className="btn btn-sm btn-primary"
              onClick={() => setIsModalOpenSettings(true)}
            >
              ⚙ Notice Settings
            </button>
          </div>
        </div>

        <div className="content">
          <div className="stat-grid mb16">
            <div className="stat-card">
              <div className="stat-icon">⏳</div>
              <div className="stat-label">Pending</div>
              <div className="stat-value">{vacateData?.pendingCount ?? 0}</div>
              <div className="stat-delta delta-neu">Awaiting decision</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">✅</div>
              <div className="stat-label">Approved</div>
              <div className="stat-value">{vacateData?.approvedCount ?? 0}</div>
              <div className="stat-delta delta-up">Notice period active</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">✕</div>
              <div className="stat-label">Rejected</div>
              <div className="stat-value">{vacateData?.rejectedCount ?? 0}</div>
              <div className="stat-delta delta-dn">Declined</div>
            </div>
            <div className="stat-card">
              <div className="stat-icon">📅</div>
              <div className="stat-label">Total</div>
              <div className="stat-value">{vacateData?.totalCount ?? 0}</div>
              <div className="stat-delta delta-neu">All requests</div>
            </div>
          </div>

          <div className="card mb14">
            <div className="flex-between mb10">
              <div className="card-title" style={{ margin: 0 }}>
                Vacate Requests
              </div>
              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <div
                  className="info-box"
                  style={{ margin: 0, fontSize: 11, padding: "6px 12px" }}
                >
                  ⚙ Min notice :&nbsp;
                  <strong>{settingsForm.minimumNoticePeriod} days</strong>
                  &nbsp;·&nbsp; Auto-approve :&nbsp;
                  <strong>{settingsForm.autoApprove ? "Yes" : "No"}</strong>
                </div>
              </div>
            </div>

            <div style={{ overflowX: "auto", marginTop: "8px" }}>
              <table>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Tenant</th>
                    <th>Request Date</th>
                    <th>Leave Date</th>
                    <th>Notice Period</th>
                    <th>Free Hold Date</th>
                    <th>Reason</th>
                    <th>Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {listLoading ? (
                    Array.from({ length: 4 }).map((_, i) => (
                      <tr key={i}>
                        {Array.from({ length: 9 }).map((_, j) => (
                          <td key={j}>
                            <div
                              style={{
                                height: 14,
                                borderRadius: 6,
                                background:
                                  "linear-gradient(90deg, var(--bg-secondary) 25%, var(--border-light) 50%, var(--bg-secondary) 75%)",
                                backgroundSize: "200% 100%",
                                animation: "shimmer 1.4s infinite",
                                width: "70%",
                              }}
                            />
                          </td>
                        ))}
                      </tr>
                    ))
                  ) : vacateData?.requests?.length === 0 ? (
                    <tr>
                      <td
                        colSpan={9}
                        style={{
                          textAlign: "center",
                          color: "var(--text-tertiary)",
                          padding: 24,
                        }}
                      >
                        No vacate requests found.
                      </td>
                    </tr>
                  ) : (
                    vacateData?.requests?.map((req, index) => {
                      const badge = getStatusBadge(req.status);
                      return (
                        <tr key={req.id}>
                          <td className="sn-col">{index + 1}</td>
                          <td>
                            <div style={{ fontWeight: 600, fontSize: 13 }}>
                              {req.tenantName}
                            </div>
                            <div
                              style={{
                                fontSize: 10,
                                color: "var(--text-tertiary)",
                              }}
                            >
                              ID: {req.tenantId}
                            </div>
                          </td>
                          <td style={{ fontSize: 12 }}>
                            {formatDate(req.requestDate)}
                          </td>
                          <td>
                            <div style={{ fontSize: 12, fontWeight: 600 }}>
                              {formatDate(req.plannedLeaveDate)}
                            </div>
                          </td>
                          <td style={{ fontSize: 12 }}>
                            {req.noticePeriodDays} days
                          </td>
                          <td>
                            {req.freeHoldDate ? (
                              <div style={{ fontSize: 12, fontWeight: 600 }}>
                                {formatDate(req.freeHoldDate)}
                              </div>
                            ) : (
                              <span style={{ fontSize: 11, color: "#9ca3af" }}>
                                —
                              </span>
                            )}
                          </td>
                          <td
                            style={{
                              fontSize: 11,
                              color: "var(--text-secondary)",
                              maxWidth: 140,
                            }}
                          >
                            {req.reasonForVacating || "—"}
                          </td>
                          <td>
                            <span
                              style={{
                                background: badge.bg,
                                color: badge.color,
                                borderRadius: 99,
                                padding: "3px 10px",
                                fontSize: 10,
                                fontWeight: 700,
                                whiteSpace: "nowrap",
                                border: `0.5px solid ${badge.color}33`,
                              }}
                            >
                              {badge.label}
                            </span>
                          </td>
                          <td>
                            <div className="td-actions">
                              {req.status === 1 ? (
                                <>
                                  <span
                                    className="action-icon green"
                                    title="Approve"
                                    onClick={() => {
                                      setSelectedRequestId(req.id);
                                      setIsModalOpenApprove(true);
                                    }}
                                  >
                                    <span className="tip">Approve</span>✓
                                  </span>
                                  <span
                                    className="action-icon red"
                                    title="Reject"
                                    onClick={() => {
                                      setSelectedRequestId(req.id);
                                      setIsModalOpenReject(true);
                                    }}
                                  >
                                    <span className="tip">Reject</span>✕
                                  </span>
                                  <span
                                    className="action-icon"
                                    title="Edit Dates"
                                    onClick={() => openEditModal(req.id)}
                                  >
                                    <span className="tip">Edit Dates</span>✎
                                  </span>
                                </>
                              ) : (
                                <span
                                  style={{
                                    fontSize: 11,
                                    color: "var(--text-tertiary)",
                                  }}
                                >
                                  —
                                </span>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>

      {/* ── Approve Modal ── */}
      {isModalOpenApprove && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 440 }}>
            <div className="modal-header">
              <div className="modal-title">✓ Approve Vacate Request</div>
              <button
                className="modal-close"
                onClick={() => {
                  setIsModalOpenApprove(false);
                  setApproveRemark("");
                }}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p
                style={{
                  fontSize: 13,
                  color: "var(--text-secondary)",
                  marginBottom: 14,
                }}
              >
                Are you sure you want to <strong>approve</strong> this vacate
                request? The tenant will be notified.
              </p>
              <div className="form-group">
                <label>
                  Owner Remark{" "}
                  <span style={{ fontWeight: 400, color: "#9ca3af" }}>
                    (optional)
                  </span>
                </label>
                <textarea
                  value={approveRemark}
                  onChange={(e) => setApproveRemark(e.target.value)}
                  placeholder="e.g. Approved. Please ensure room is cleaned before leaving."
                  style={{ minHeight: 70 }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={() => {
                  setIsModalOpenApprove(false);
                  setApproveRemark("");
                }}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-success"
                onClick={handleApprove}
                disabled={actionLoading}
              >
                {actionLoading ? "Approving..." : "✓ Yes, Approve"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Reject Modal ── */}
      {isModalOpenReject && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 440 }}>
            <div className="modal-header">
              <div className="modal-title">✕ Reject Vacate Request</div>
              <button
                className="modal-close"
                onClick={() => {
                  setIsModalOpenReject(false);
                  setRejectRemark("");
                }}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <p
                style={{
                  fontSize: 13,
                  color: "var(--text-secondary)",
                  marginBottom: 14,
                }}
              >
                Are you sure you want to <strong>reject</strong> this vacate
                request? The tenant will be notified.
              </p>
              <div className="form-group">
                <label>
                  Owner Remark{" "}
                  <span style={{ fontWeight: 400, color: "#9ca3af" }}>
                    (optional)
                  </span>
                </label>
                <textarea
                  value={rejectRemark}
                  onChange={(e) => setRejectRemark(e.target.value)}
                  placeholder="e.g. Rejected due to pending dues. Please clear dues first."
                  style={{ minHeight: 70 }}
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={() => {
                  setIsModalOpenReject(false);
                  setRejectRemark("");
                }}
                disabled={actionLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-danger"
                onClick={handleReject}
                disabled={actionLoading}
              >
                {actionLoading ? "Rejecting..." : "✕ Yes, Reject"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Notice Setting Modal ── */}
      {isModalOpenSettings && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 480 }}>
            <div className="modal-header">
              <div className="modal-title">⚙ Vacate Notice Settings</div>
              <button
                className="modal-close"
                onClick={() => setIsModalOpenSettings(false)}
              >
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group mb12">
                <label>Minimum Notice Period (days)</label>
                <input
                  type="number"
                  name="minimumNoticePeriod"
                  value={settingsForm.minimumNoticePeriod}
                  onChange={handleSettingsChange}
                  min={1}
                  max={90}
                />
                <div className="input-hint">
                  Tenant must give this many days notice before leaving
                </div>
              </div>
              <div className="form-group mb12">
                <label>Short Notice Penalty (₹ per day)</label>
                <input
                  type="number"
                  name="shortNoticePenalty"
                  value={settingsForm.shortNoticePenalty}
                  onChange={handleSettingsChange}
                  min={0}
                />
                <div className="input-hint">
                  Penalty charged per day when tenant gives less than minimum
                  notice
                </div>
              </div>
              <div
                className="form-group"
                style={{ display: "flex", alignItems: "center", gap: 12 }}
              >
                <label style={{ margin: 0 }}>Auto-Approve Requests</label>
                <label className="toggle">
                  <input
                    type="checkbox"
                    name="autoApprove"
                    checked={settingsForm.autoApprove}
                    onChange={handleSettingsChange}
                  />
                  <span className="toggle-slider" />
                </label>
                <span style={{ fontSize: 11, color: "var(--text-secondary)" }}>
                  When ON, requests are auto-approved immediately
                </span>
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={() => setIsModalOpenSettings(false)}
                disabled={settingsLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSaveSettings}
                disabled={settingsLoading}
              >
                {settingsLoading ? "Saving..." : "💾 Save Settings"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Edit Vacate Modal ── */}
      {isModalOpenEdit && (
        <div className="modal-overlay open">
          <div className="modal" style={{ width: 480 }}>
            <div className="modal-header">
              <div className="modal-title">✎ Edit Vacate Request</div>
              <button className="modal-close" onClick={closeEditModal}>
                ✕
              </button>
            </div>
            <div className="modal-body">
              <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 16 }}>
                Tenant: {selectedEditRequest?.tenantName}
              </div>

              <div className="form-group mb12">
                <label>Leave Date *</label>
                <input
                  type="date"
                  name="plannedLeaveDate"
                  value={editForm.plannedLeaveDate}
                  onChange={handleEditChange}
                />
                <div className="input-hint">
                  Minimum notice: {settingsForm.minimumNoticePeriod} days from
                  request date ({formatDate(selectedEditRequest?.requestDate)})
                </div>
              </div>

              <div className="form-group mb12">
                <label>Free Hold Date</label>
                <input
                  type="date"
                  name="freeHoldDate"
                  value={editForm.freeHoldDate}
                  onChange={handleEditChange}
                />
                <div className="input-hint">
                  Date when bed/room becomes available for new tenant
                </div>
              </div>

              <div className="form-group">
                <label>Owner Notes</label>
                <textarea
                  name="ownerNotes"
                  value={editForm.ownerNotes}
                  onChange={handleEditChange}
                  style={{ minHeight: 60 }}
                  placeholder="Optional notes…"
                />
              </div>
            </div>
            <div className="modal-footer">
              <button
                className="btn"
                onClick={closeEditModal}
                disabled={editLoading}
              >
                Cancel
              </button>
              <button
                className="btn btn-primary"
                onClick={handleSaveEdit}
                disabled={editLoading}
              >
                {editLoading ? "Saving..." : "💾 Save Changes"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
